/** App shell + routing. Everything under the shell requires a session; without one we send to login. */
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth';
import { Wordmark } from './ui';
import { Login } from './pages/Login';
import { Sites } from './pages/Sites';
import { SiteDetail } from './pages/SiteDetail';
import { NewSiteWizard } from './pages/NewSiteWizard';
import { NewController } from './pages/NewController';
import { ControllerPanel } from './pages/ControllerPanel';
import { EngineerPanel } from './pages/EngineerPanel';
import { Team } from './pages/Team';
import { Notifications } from './pages/Notifications';
import { Alarms } from './pages/Alarms';
import { Platform } from './pages/Platform';
import { VictronHome, VictronSystem } from './pages/VictronSystem';

/**
 * A site owner (owner_operator) gets their own system and nothing else: no nav, no fleet, no admin
 * pages — every path lands on their system. The API enforces the same boundary; this is so the owner
 * never sees a door they can't open.
 */
function OwnerShell({ children }: { children: JSX.Element }): JSX.Element {
  const { signOut } = useAuth();
  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar-left">
          <Wordmark />
        </div>
        <div className="right">
          <button className="btn btn-ghost btn-sm" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </header>
      <main className="main">{children}</main>
    </div>
  );
}

function Shell({ children }: { children: JSX.Element }): JSX.Element {
  const { session, signOut } = useAuth();
  const isAdmin = session?.role === 'tenant_admin';
  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar-left">
          <Wordmark />
          <nav className="topnav">
            <NavLink to="/sites">Sites</NavLink>
            <NavLink to="/alarms">Alarms</NavLink>
            {isAdmin && <NavLink to="/team">Team</NavLink>}
            {isAdmin && <NavLink to="/notifications">Notifications</NavLink>}
          </nav>
        </div>
        <div className="right">
          <div className="who">
            <div className="tenant">{session?.tenant.name}</div>
            <div className="role">{session?.role}</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </header>
      <main className="main">{children}</main>
    </div>
  );
}

function BootSplash(): JSX.Element {
  return (
    <div className="center-screen">
      <div style={{ opacity: 0.7 }}>
        <Wordmark />
      </div>
    </div>
  );
}

function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const { session, platform, restoring } = useAuth();
  const location = useLocation();
  if (restoring) return <BootSplash />;
  if (platform) return <Navigate to="/platform" replace />;
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <Shell>{children}</Shell>;
}

function RequirePlatform({ children }: { children: JSX.Element }): JSX.Element {
  const { platform, restoring } = useAuth();
  if (restoring) return <BootSplash />;
  if (!platform) return <Navigate to="/login" replace />;
  return children;
}

export function App(): JSX.Element {
  const { session, restoring } = useAuth();
  if (!restoring && session?.role === 'owner_operator') {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="*"
          element={
            <OwnerShell>
              <VictronHome />
            </OwnerShell>
          }
        />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/platform"
        element={
          <RequirePlatform>
            <Platform />
          </RequirePlatform>
        }
      />
      <Route
        path="/sites"
        element={
          <RequireAuth>
            <Sites />
          </RequireAuth>
        }
      />
      <Route
        path="/sites/new"
        element={
          <RequireAuth>
            <NewSiteWizard />
          </RequireAuth>
        }
      />
      <Route
        path="/sites/:id"
        element={
          <RequireAuth>
            <SiteDetail />
          </RequireAuth>
        }
      />
      <Route
        path="/sites/:id/controllers/new"
        element={
          <RequireAuth>
            <NewController />
          </RequireAuth>
        }
      />
      <Route
        path="/controllers/:id"
        element={
          <RequireAuth>
            <ControllerPanel />
          </RequireAuth>
        }
      />
      <Route
        path="/controllers/:id/engineer"
        element={
          <RequireAuth>
            <EngineerPanel />
          </RequireAuth>
        }
      />
      <Route
        path="/victron/:id"
        element={
          <RequireAuth>
            <VictronSystem />
          </RequireAuth>
        }
      />
      <Route
        path="/alarms"
        element={
          <RequireAuth>
            <Alarms />
          </RequireAuth>
        }
      />
      <Route
        path="/team"
        element={
          <RequireAuth>
            <Team />
          </RequireAuth>
        }
      />
      <Route
        path="/notifications"
        element={
          <RequireAuth>
            <Notifications />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/sites" replace />} />
    </Routes>
  );
}
